import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ReviewForm, { ReviewsList } from "../components/ReviewForm";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Star, ExternalLink, Loader2, MessageSquare, ThumbsUp, Award
} from "lucide-react";

export default function ReviewsPage() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [completedJobs, setCompletedJobs] = useState([]);
  const token = localStorage.getItem("token");

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reviews/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
      
      if (response.data.length > 0) {
        const avg = response.data.reduce((sum, r) => sum + r.overall_rating, 0) / response.data.length;
        setStats({ total: response.data.length, average: avg.toFixed(1) });
      }
    } catch (error) {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchCompletedJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter completed jobs that don't have reviews yet
      const completed = response.data.filter(job => job.status === "completed");
      setCompletedJobs(completed);
    } catch (error) {
      toast.error("Failed to load completed jobs");
    }
  }, [token]);

  useEffect(() => {
    fetchReviews();
    if (user?.user_type === "customer") {
      fetchCompletedJobs();
    }
  }, [user, fetchReviews, fetchCompletedJobs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto" data-testid="reviews-page">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 mb-2">
            {user?.user_type === "professional" ? "Your Reviews" : "Reviews You've Written"}
          </h1>
          <p className="text-gray-600">
            {user?.user_type === "professional" 
              ? "See what customers are saying about your work"
              : "Manage reviews for completed jobs"}
          </p>
        </div>

        {/* Stats Card */}
        {user?.user_type === "professional" && reviews.length > 0 && (
          <Card className="border-stone-200 mb-6 bg-gradient-to-r from-yellow-50 to-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">{stats.average}</p>
                    <p className="text-gray-600">{stats.total} review{stats.total !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                
                {/* Google Review Button */}
                <a 
                  href="https://www.google.com/maps/search/Best+Quality+Leads"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden md:flex"
                >
                  <Button className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-full">
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Leave a Google Review
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Google Review CTA for all users */}
        <Card className="border-blue-200 bg-blue-50 mb-6">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-blue-900 font-medium">Help us grow!</p>
                  <p className="text-blue-700 text-sm">Leave a review on Google to help others find Best Quality Leads</p>
                </div>
              </div>
              <a 
                href="https://www.google.com/maps/search/Best+Quality+Leads"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 rounded-full" data-testid="google-review-btn">
                  Find Us on Google
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Reviews List */}
        <Card className="border-stone-200">
          <CardContent className="pt-6">
            <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-forest" />
              {user?.user_type === "professional" ? "Customer Reviews" : "Your Reviews"}
            </h2>
            
            {reviews.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Star className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No reviews yet</p>
                <p className="text-sm mt-1">
                  {user?.user_type === "professional" 
                    ? "Complete jobs to start receiving reviews"
                    : "Review professionals after completing jobs"}
                </p>
              </div>
            ) : (
              <ReviewsList reviews={reviews} />
            )}
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
